import { useState, useEffect } from "react";
import { Home, Loader2 } from "lucide-react";
import "./space.css";
import { loadState, saveState, corridorComplete } from "./stateManager";
import StageOneTrek from "./StageOneTrek";
import BaseMapShell from "./BaseMapShell";
import { spaceApi } from "./spaceApi";

const LOGO = "/assets/space/mist_logo.jpg";

// Space Supremacy game root. Splash → Stage-1 trek (until the corridor is sealed)
// → Sanctuary Base Map shell. On enter it reconciles progress with the server
// (authoritative save), pushing local progress up if it is further ahead.
export default function SpaceApp({ onBack }) {
  const [entered, setEntered] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [atBase, setAtBase] = useState(() => corridorComplete(loadState()));

  useEffect(() => {
    if (!entered) return;
    let alive = true;
    (async () => {
      setSyncing(true);
      try {
        const local = loadState();
        const s = await spaceApi.getState();
        const localSum = (local.corridor || []).reduce((a, b) => a + b, 0);
        const srvSum = (s.corridor || []).reduce((a, b) => a + b, 0);
        let corridor = s.corridor, heroes = s.heroes;
        if (localSum > srvSum) {
          const up = await spaceApi.syncProgress({ corridor: local.corridor, heroes: local.heroes });
          corridor = up.corridor; heroes = up.heroes;
        }
        saveState({ ...local, corridor, heroes: { ...local.heroes, ...heroes }, commandCentreLevel: s.commandCentreLevel, phase: s.phase });
        if (alive) setAtBase((corridor || []).every((v) => v === 3));
      } catch {
        if (alive) setAtBase(corridorComplete(loadState()));
      } finally {
        if (alive) setSyncing(false);
      }
    })();
    return () => { alive = false; };
  }, [entered]);

  if (!entered) {
    return (
      <div className="ss-viewport" data-testid="space-supremacy-app">
        <div className="ss-splash" data-testid="space-splash">
          <img src={LOGO} alt="MiST: The Rise of Civilisation" className="ss-splash-logo" />
          <div className="ss-splash-scrim" />
          <button className="ss-enter-btn" data-testid="space-enter" onClick={() => setEntered(true)}>
            Enter Command
          </button>
        </div>
        <button className="ss-home-btn" data-testid="space-home-btn" onClick={onBack} aria-label="Back to Games Hub">
          <Home size={18} />
        </button>
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="ss-viewport" data-testid="space-supremacy-app">
        <div className="ss-atmosphere" />
        <button className="ss-home-btn" data-testid="space-home-btn" onClick={onBack}><Home size={18} /></button>
        <div className="bg-loading" data-testid="space-sync-loading"><Loader2 size={28} className="bg-spin" /><span>Syncing colony…</span></div>
      </div>
    );
  }

  if (atBase) return <BaseMapShell onHome={onBack} />;
  return <StageOneTrek onHome={onBack} onComplete={() => setAtBase(true)} />;
}
